import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  LexicalCommand,
  LexicalNode,
  NodeKey,
  ParagraphNode,
} from 'lexical';
import { useEffect, useRef } from 'react';
import {
  $createPlaceholderNode,
  $isPlaceholderNode,
  PlaceholderNode,
} from '../nodes/PlaceholderNode';
import { parseTemplate } from '../textTemplates';

export interface InsertTemplatePayload {
  content: string;
  values?: Record<string, string>;
}

export const INSERT_TEMPLATE_COMMAND: LexicalCommand<InsertTemplatePayload> =
  createCommand('INSERT_TEMPLATE_COMMAND');

export const NEXT_PLACEHOLDER_COMMAND: LexicalCommand<void> = createCommand(
  'NEXT_PLACEHOLDER_COMMAND',
);

interface TemplateSession {
  placeholderKeys: NodeKey[];
  endKey: NodeKey;
}

function $selectPlaceholder(node: PlaceholderNode) {
  node.select(0, node.getTextContentSize());
}

function $livePlaceholders(session: TemplateSession): PlaceholderNode[] {
  return session.placeholderKeys
    .map((key) => $getNodeByKey(key))
    .filter($isPlaceholderNode);
}

// Filled tab stops become plain text; untouched ones stay marked for the save check.
function $endSession(session: TemplateSession) {
  for (const node of $livePlaceholders(session)) {
    if (node.isFilled()) {
      const text = $createTextNode(node.getTextContent());
      text.setFormat(node.getFormat());
      text.setStyle(node.getStyle());
      node.replace(text);
    }
  }
}

function $buildParagraphs(
  payload: InsertTemplatePayload,
  format: number,
  style: string,
): { paragraphs: ParagraphNode[]; placeholders: PlaceholderNode[] } {
  const placeholders: PlaceholderNode[] = [];
  const paragraphs = parseTemplate(payload.content).map((segments) => {
    const paragraph = $createParagraphNode();
    for (const segment of segments) {
      const node =
        segment.type === 'text'
          ? $createTextNode(segment.text)
          : $createPlaceholderNode(
              segment.name,
              payload.values?.[segment.name] || undefined,
            );
      node.setFormat(format).setStyle(style);
      if ($isPlaceholderNode(node)) {
        placeholders.push(node);
      }
      paragraph.append(node);
    }
    return paragraph;
  });
  return { paragraphs, placeholders };
}

function $insertParagraphs(paragraphs: ParagraphNode[]) {
  const root = $getRoot();
  const selection = $getSelection();
  if (root.getTextContentSize() === 0) {
    root.clear().append(...paragraphs);
  } else if ($isRangeSelection(selection)) {
    selection.insertNodes(paragraphs);
  } else {
    root.append(...paragraphs);
  }
}

export function TemplatePlugin() {
  const [editor] = useLexicalComposerContext();
  const sessionRef = useRef<TemplateSession | null>(null);

  useEffect(() => {
    if (!editor.hasNodes([PlaceholderNode])) {
      throw new Error('TemplatePlugin: PlaceholderNode not registered');
    }

    const finish = () => {
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) {
        $endSession(session);
      }
      return session;
    };

    const jump = (step: 1 | -1): boolean => {
      const session = sessionRef.current;
      if (!session) {
        return false;
      }
      const placeholders = $livePlaceholders(session);
      const selection = $getSelection();
      const current = $isRangeSelection(selection)
        ? selection.anchor.getNode()
        : null;
      const index = placeholders.findIndex((node) => node.is(current));
      const target = placeholders[index === -1 ? 0 : index + step];

      if (target) {
        $selectPlaceholder(target);
      } else if (step === 1) {
        const ended = finish();
        const end: LexicalNode | null = ended && $getNodeByKey(ended.endKey);
        if ($isElementNode(end)) {
          end.selectEnd();
        }
      }
      return true;
    };

    const isInPlaceholder = () => {
      const session = sessionRef.current;
      const selection = $getSelection();
      return (
        session !== null &&
        $isRangeSelection(selection) &&
        session.placeholderKeys.includes(selection.anchor.key)
      );
    };

    return mergeRegister(
      editor.registerCommand(
        INSERT_TEMPLATE_COMMAND,
        (payload) => {
          finish();
          const selection = $getSelection();
          const { paragraphs, placeholders } = $buildParagraphs(
            payload,
            $isRangeSelection(selection) ? selection.format : 0,
            $isRangeSelection(selection) ? selection.style : '',
          );
          if (paragraphs.length === 0) {
            return true;
          }
          $insertParagraphs(paragraphs);

          const last = paragraphs[paragraphs.length - 1];
          if (placeholders.length === 0) {
            last.selectEnd();
            return true;
          }
          sessionRef.current = {
            placeholderKeys: placeholders.map((node) => node.getKey()),
            endKey: last.getKey(),
          };
          $selectPlaceholder(placeholders[0]);
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        NEXT_PLACEHOLDER_COMMAND,
        () => jump(1),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          if (!jump(event.shiftKey ? -1 : 1)) {
            return false;
          }
          event.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (!isInPlaceholder() || event?.shiftKey) {
            return false;
          }
          event?.preventDefault();
          return jump(1);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => finish() !== null,
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerUpdateListener(({ editorState }) => {
        const session = sessionRef.current;
        if (
          session &&
          editorState.read(() => $livePlaceholders(session).length === 0)
        ) {
          sessionRef.current = null;
        }
      }),
    );
  }, [editor]);

  return null;
}
